<script setup lang="ts">
    import { IonButton, IonSpinner } from '@ionic/vue';
    import { defineProps, toRefs } from 'vue';
    import router from '@/router';

    type Color =
        | 'primary'
        | 'secondary'
        | 'tertiary'
        | 'success'
        | 'warning'
        | 'danger'
        | 'light'
        | 'medium'
        | 'dark';
    type Expand = 'block' | 'full' | 'default';
    type Size = 'small' | 'default' | 'large';
    type Shape = 'round';
    type Fill = 'solid' | 'outline' | 'clear';
    type Spinner =
        | 'dots'
        | 'lines'
        | 'lines-small'
        | 'lines-sharp'
        | 'lines-sharp-small'
        | 'bubbles'
        | 'circles'
        | 'circular'
        | 'crescent';
    type IconSlot = 'start' | 'end' | 'icon-only';

    interface Props {
        color?: Color;
        expand?: Expand;
        size?: Size;
        shape?: Shape;
        isLoading?: boolean;
        disabled?: boolean;
        spinner?: Spinner;
        fill?: Fill;
        to?: any;
        download?: boolean;
        href?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        color: 'primary',
        expand: 'block',
        size: 'default',
        isLoading: false,
        disabled: false,
        spinner: 'crescent',
        fill: 'solid',
        download: false,
    });

    const { to } = toRefs(props);

    const redirectToRoute = () => {
        if (!to.value) return;
        router.push(to.value);
    };
</script>

<template>
    <ion-button
        :color="color"
        :expand="expand === 'default' ? undefined : expand"
        :size="size"
        :shape="shape"
        :disabled="isLoading || disabled"
        :fill="fill"
        :href="href"
        @click="!!to && redirectToRoute()"
    >
        <slot />
        <ion-spinner
            v-if="isLoading"
            slot="end"
            class="ml-2 size-4"
            :name="spinner"
        ></ion-spinner>
    </ion-button>
</template>

<style scoped>
    ion-button {
        --border-radius: 8px;
        font-weight: 400;
    }

    ion-button.button-large {
        min-height: 64px;
    }

    ion-button.button-default {
        min-height: 56px;
    }

    ion-button.button-small {
        min-height: 36px;
    }
</style>
